import React, { useState, useEffect, useCallback } from "react";

const API = "";

/* ─── Types ──────────────────────────────────────────────────────────────── */
interface User { _id:string; phone?:string; username?:string; nickname?:string; balance:number; createdAt:string; isActive?:boolean; role?:string; lastLogin?:string; }
interface Ledger { _id:string; userId:any; type:string; amount:number; balanceBefore:number; balanceAfter:number; reference:string; roundId?:string; createdAt:string; }
interface Stats { totalUsers:number; totalBets:number; totalWins:number; totalWagered:number; totalPaidOut:number; houseEdge:string; currentRound:{GameState:string;multiplier:number;crashPoint:number;roundId:string}; }
interface Withdrawal { _id:string; phone:string; amount:number; status:string; reference:string; mpesaCode?:string; createdAt:string; processedAt?:string; user?:User; }
interface Live { bettedUsers:any[]; gameState:string; multiplier:number; history:number[]; }

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const API_CALL = async (path:string, token:string, method="GET", body?:any) => {
  const r = await fetch(`${API}${path}`, { method, headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`}, body:body?JSON.stringify(body):undefined });
  return r.json();
};
const dn = (u:User) => u.nickname || u.username || u.phone || "—";
const fmt = (n:number) => Number(n||0).toLocaleString("en-KE");
const S = { red:"#ef4444",green:"#22c55e",blue:"#60a5fa",yellow:"#facc15",orange:"#fb923c",purple:"#a78bfa",pink:"#f472b6",teal:"#2dd4bf",gray:"#6b7280" };
const card = (x?:React.CSSProperties):React.CSSProperties => ({ background:"linear-gradient(135deg,#1a1a2e 0%,#161625 100%)",border:"1px solid #252545",borderRadius:"12px",padding:"20px",boxShadow:"0 4px 24px rgba(0,0,0,0.4)",backdropFilter:"blur(4px)",...x });

/* ─── Primitives ─────────────────────────────────────────────────────────── */
const BADGE_MAP:any = { green:["#14532d","#22c55e"],red:["#7f1d1d","#ef4444"],blue:["#1e3a5f","#60a5fa"],yellow:["#713f12","#facc15"],gray:["#1f2937","#9ca3af"],purple:["#3b0764","#a78bfa"],orange:["#431407","#fb923c"],teal:["#134e4a","#2dd4bf"] };

function Badge({text,color="gray"}:{text:string;color?:string}) {
  const [bg,fg] = BADGE_MAP[color]||BADGE_MAP.gray;
  return <span style={{background:bg,color:fg,padding:"3px 9px",borderRadius:"12px",fontSize:"11px",fontWeight:"bold",whiteSpace:"nowrap"}}>{text}</span>;
}

function Btn({label,color="gray",onClick,sm,full,disabled}:{label:any;color?:string;onClick:()=>void;sm?:boolean;full?:boolean;disabled?:boolean}) {
  const C:any={red:"#ef4444",green:"#16a34a",blue:"#2563eb",orange:"#ff4500",gray:"#374151",yellow:"#b45309",purple:"#7c3aed",teal:"#0d9488"};
  const bg=C[color]||"#374151";
  return <button onClick={onClick} disabled={disabled} style={{background:disabled?"#1a1a2e":`linear-gradient(135deg,${bg},${bg}dd)`,color:disabled?"#444":"#fff",border:disabled?"1px solid #252545":"none",padding:sm?"5px 12px":"10px 18px",borderRadius:"8px",cursor:disabled?"not-allowed":"pointer",fontSize:sm?"11px":"13px",fontWeight:"bold",width:full?"100%":undefined,opacity:disabled?0.5:1,boxShadow:disabled?"none":`0 2px 8px ${bg}44`,letterSpacing:"0.2px",whiteSpace:"nowrap"}}>{label}</button>;
}

function Inp({placeholder,value,onChange,type="text",style}:any) {
  return <input type={type} placeholder={placeholder} value={value} onChange={(e:any)=>onChange(e.target.value)} style={{background:"rgba(0,0,0,0.3)",border:"1px solid #252545",color:"#e2e8f0",padding:"10px 14px",borderRadius:"8px",fontSize:"13px",outline:"none",transition:"border-color 0.2s",width:"100%",boxSizing:"border-box",...style}} />;
}

function Modal({title,children,onClose,width=400}:{title:string;children:React.ReactNode;onClose:()=>void;width?:number}) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.88)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,backdropFilter:"blur(4px)"}}>
      <div style={{background:"linear-gradient(160deg,#1e1e38,#151525)",border:"1px solid #2a2a50",borderRadius:"16px",width,maxWidth:"96vw",maxHeight:"92vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(0,0,0,0.7)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 24px",borderBottom:"1px solid #252545",background:"linear-gradient(90deg,rgba(255,69,0,0.08),transparent)"}}>
          <div style={{color:"#ff4500",fontWeight:"bold",fontSize:"15px",letterSpacing:"0.3px"}}>{title}</div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,0.05)",border:"1px solid #333",color:"#888",cursor:"pointer",fontSize:"18px",lineHeight:1,width:"28px",height:"28px",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
        </div>
        <div style={{padding:"20px 24px"}}>{children}</div>
      </div>
    </div>
  );
}

function Tbl({cols,rows,empty="No data"}:{cols:string[];rows:React.ReactNode[][];empty?:string}) {
  if (!rows.length) return <div style={{textAlign:"center",color:"#555",padding:"40px",fontSize:"13px",letterSpacing:"0.5px"}}>{empty}</div>;
  return (
    <div style={{overflowX:"auto",borderRadius:"8px",border:"1px solid #252545"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:"13px",minWidth:"500px"}}>
        <thead><tr style={{background:"linear-gradient(90deg,#1a1a3e,#161630)",borderBottom:"1px solid #252545"}}>{cols.map(c=><th key={c} style={{padding:"11px 14px",color:"#6b7280",textAlign:"left",fontWeight:600,whiteSpace:"nowrap",fontSize:"11px",textTransform:"uppercase",letterSpacing:"0.8px"}}>{c}</th>)}</tr></thead>
        <tbody>{rows.map((row,i)=><tr key={i} style={{borderBottom:"1px solid #13132a",background:i%2===0?"transparent":"rgba(255,255,255,0.01)",transition:"background 0.15s"}}>{row.map((cell,j)=><td key={j} style={{padding:"10px 14px"}}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function KPI({label,val,color="#fff",sub}:{label:string;val:any;color?:string;sub?:string}) {
  return (
    <div style={{...card({padding:"0",overflow:"hidden"})}}>
      <div style={{height:"3px",background:color,borderRadius:"12px 12px 0 0"}}/>
      <div style={{padding:"14px 16px",textAlign:"center"}}>
        <div style={{color:"#666",fontSize:"10px",textTransform:"uppercase",letterSpacing:"1px",marginBottom:"6px",fontWeight:600}}>{label}</div>
        <div style={{color,fontSize:"22px",fontWeight:"bold",letterSpacing:"-0.5px"}}>{val}</div>
        {sub&&<div style={{color:"#555",fontSize:"10px",marginTop:"4px"}}>{sub}</div>}
      </div>
    </div>
  );
}

function Alert({text,ok}:{text:string;ok:boolean}) {
  return <div style={{background:ok?"rgba(34,197,94,0.08)":"rgba(239,68,68,0.08)",border:`1px solid ${ok?"#22c55e44":"#ef444444"}`,color:ok?"#22c55e":"#ef4444",padding:"12px 16px",borderRadius:"10px",marginBottom:"16px",fontSize:"13px",display:"flex",alignItems:"center",gap:"8px"}}><span>{ok?"✅":"⚠️"}</span>{text}</div>;
}

/* ─── Live hook (polls live endpoint) ───────────────────────────────────────*/
function useLive(token:string) {
  const [live,setLive] = useState<Live>({bettedUsers:[],gameState:"BET",multiplier:1,history:[]});
  useEffect(()=>{
    const load=()=>API_CALL("/api/admin/game/live",token).then((d:any)=>{if(d.status){setLive({bettedUsers:d.bettedUsers||[],gameState:d.gameState?.GameState||"BET",multiplier:d.gameState?.multiplier||1,history:[]});}}).catch(()=>{});
    load(); const t=setInterval(load,1500); return ()=>clearInterval(t);
  },[token]);
  return live;
}

/* ══════════════════════════ DASHBOARD ══════════════════════════════════════ */
function Dashboard({token,stats}:{token:string;stats:Stats|null}) {
  const live = useLive(token);
  const [feed,setFeed] = useState<Ledger[]>([]);
  const [wins,setWins] = useState<Ledger[]>([]);
  const loadFeed = useCallback(async()=>{
    const [a,b] = await Promise.all([API_CALL("/api/admin/ledger?limit=12",token),API_CALL("/api/admin/ledger?type=win&limit=12",token)]);
    setFeed(a.data||[]); setWins(b.data||[]);
  },[token]);
  useEffect(()=>{loadFeed();const t=setInterval(loadFeed,5000);return()=>clearInterval(t);},[loadFeed]);

  const gs=stats?.currentRound;
  const profit=(stats?.totalWagered||0)-(stats?.totalPaidOut||0);
  const staked=live.bettedUsers.reduce((s:number,u:any)=>s+(u.betAmount||0),0);
  const gsColor=live.gameState==="PLAYING"?"green":live.gameState==="BET"?"blue":"red";

  return (
    <div>
      <h2 style={{color:"#ff4500",marginBottom:"20px"}}>📊 Live Operations Dashboard</h2>
      {/* Status bar */}
      <div style={{...card({marginBottom:"18px",padding:"13px 20px"}),borderColor:live.gameState==="PLAYING"?"#22c55e":"#2a2a4e",display:"flex",alignItems:"center",gap:"20px",flexWrap:"wrap"}}>
        <span style={{width:"8px",height:"8px",borderRadius:"50%",background:live.gameState==="PLAYING"?S.green:"#555",display:"inline-block",boxShadow:live.gameState==="PLAYING"?"0 0 8px #22c55e":"none"}} />
        <Badge text={live.gameState||"—"} color={gsColor} />
        <span style={{color:S.yellow,fontSize:"24px",fontWeight:"bold"}}>{live.multiplier?.toFixed(2)}x</span>
        <span style={{color:"#555",fontSize:"12px"}}>Crash @ <span style={{color:S.red}}>{gs?.crashPoint}x</span></span>
        <span style={{color:"#888",fontSize:"12px"}}>{live.bettedUsers.length} bets · KSh {fmt(staked)} staked</span>
        <span style={{marginLeft:"auto",color:"#444",fontSize:"11px"}}>{gs?.roundId?.slice(0,14)}…</span>
      </div>
      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:"10px",marginBottom:"18px"}}>
        <KPI label="Total Users" val={stats?.totalUsers??"-"} color={S.blue}/>
        <KPI label="Active Bets" val={live.bettedUsers.length} color={S.yellow} sub={`KSh ${fmt(staked)}`}/>
        <KPI label="Total Wagered" val={`KSh ${fmt(stats?.totalWagered||0)}`} color={S.orange}/>
        <KPI label="Total Paid" val={`KSh ${fmt(stats?.totalPaidOut||0)}`} color={S.teal}/>
        <KPI label="Profit" val={`KSh ${fmt(profit)}`} color={profit>=0?S.green:S.red}/>
        <KPI label="House Edge" val={stats?.houseEdge||"-"} color={S.pink}/>
        <KPI label="Total Bets" val={fmt(stats?.totalBets||0)} color={S.purple}/>
        <KPI label="Total Wins" val={fmt(stats?.totalWins||0)} color={S.teal}/>
      </div>
      {/* Active bets strip */}
      {live.bettedUsers.length>0&&(
        <div style={{...card({marginBottom:"18px",padding:"12px 16px"}),borderColor:"#22c55e"}}>
          <div style={{color:S.green,fontSize:"12px",fontWeight:"bold",marginBottom:"8px"}}>⚡ Active Bets This Round</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:"6px"}}>
            {live.bettedUsers.map((u:any,i:number)=>(
              <div key={i} style={{background:u.cashedOut?"#14532d":"#1e3a5f",borderRadius:"6px",padding:"5px 10px",fontSize:"12px"}}>
                <span style={{color:"#aaa"}}>{u.username}</span>
                <span style={{color:"#fff",marginLeft:"6px",fontWeight:"bold"}}>KSh {fmt(u.betAmount)}</span>
                {u.cashedOut&&<span style={{color:S.green,marginLeft:"6px"}}>✓{u.multiplier}x</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Feed columns */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"16px"}}>
        <div style={card()}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
            <h3 style={{color:"#ff4500",fontSize:"13px"}}>⚡ Recent Transactions</h3>
            <Btn label="↻" onClick={loadFeed} sm/>
          </div>
          <Tbl cols={["Type","User","Amount","Time"]} rows={feed.map(e=>{
            const u=typeof e.userId==="object"?(e.userId?.nickname||e.userId?.phone||"—"):"—";
            return [<Badge text={e.type.toUpperCase()} color={e.type==="win"||e.type==="deposit"?"green":e.type==="bet"?"blue":"yellow"}/>,<span style={{color:"#888",fontSize:"12px"}}>{u}</span>,<span style={{color:e.amount<0?S.red:S.green,fontWeight:"bold"}}>{e.amount<0?"":"+"}KSh {fmt(Math.abs(e.amount))}</span>,<span style={{color:"#555",fontSize:"11px"}}>{new Date(e.createdAt).toLocaleTimeString("en-KE")}</span>];
          })}/>
        </div>
        <div style={card()}>
          <h3 style={{color:S.green,fontSize:"13px",marginBottom:"12px"}}>💰 Recent Cashouts</h3>
          <Tbl cols={["User","Won","Time"]} rows={wins.map(e=>{
            const u=typeof e.userId==="object"?(e.userId?.nickname||e.userId?.phone||"—"):"—";
            return [<span style={{color:"#aaa"}}>{u}</span>,<span style={{color:S.green,fontWeight:"bold"}}>+KSh {fmt(e.amount)}</span>,<span style={{color:"#555",fontSize:"11px"}}>{new Date(e.createdAt).toLocaleString("en-KE")}</span>];
          })}/>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════ USERS ══════════════════════════════════════════ */
function Users({token}:{token:string}) {
  const [users,setUsers]=useState<User[]>([]);
  const [search,setSearch]=useState("");
  const [loading,setLoading]=useState(false);
  const [msg,setMsg]=useState({text:"",ok:true});
  const [dep,setDep]=useState<{user:User;amt:string}|null>(null);
  const [bal,setBal]=useState<{user:User;amt:string}|null>(null);
  const [pw,setPw]=useState<{user:User;val:string}|null>(null);
  const [prof,setProf]=useState<User|null>(null);
  const [hist,setHist]=useState<User|null>(null);
  const flash=(text:string,ok=true)=>{setMsg({text,ok});setTimeout(()=>setMsg({text:"",ok:true}),3500);};
  const load=useCallback(async()=>{setLoading(true);const d=await API_CALL("/api/admin/users",token);setUsers(d.data||[]);setLoading(false);},[token]);
  useEffect(()=>{load();},[load]);

  const doDeposit=async()=>{
    if(!dep||!dep.amt)return;
    const d=await API_CALL("/api/admin/deposit",token,"POST",{userId:dep.user._id,amount:parseFloat(dep.amt)});
    if(d.status){flash(`✅ Deposited KSh ${fmt(parseFloat(dep.amt))} → ${dn(dep.user)}`);setDep(null);load();}
    else flash("❌ "+(d.message||"Failed"),false);
  };
  const doSetBal=async()=>{
    if(!bal||!bal.amt)return;
    const d=await API_CALL("/api/admin/update-balance",token,"POST",{userId:bal.user._id,balance:parseFloat(bal.amt)});
    if(d.status){flash("✅ Balance updated");setBal(null);load();}
    else flash("❌ "+(d.message||"Failed"),false);
  };
  const doResetPw=async()=>{
    if(!pw||pw.val.length<6){flash("❌ Min 6 chars",false);return;}
    const d=await API_CALL("/api/admin/reset-password",token,"POST",{userId:pw.user._id,newPassword:pw.val});
    if(d.status){flash(`✅ Password reset for ${dn(pw.user)}`);setPw(null);}
    else flash("❌ "+(d.message||"Failed"),false);
  };
  const doSuspend=async(u:User)=>{
    if(!window.confirm(u.isActive!==false?`Suspend ${dn(u)}?`:`Activate ${dn(u)}?`))return;
    await API_CALL("/api/admin/suspend",token,"POST",{userId:u._id,suspend:u.isActive!==false});
    flash(u.isActive!==false?"🔒 Suspended":"✅ Activated");load();
  };
  const doDel=async(u:User)=>{
    if(!window.confirm(`PERMANENTLY DELETE ${dn(u)}? Cannot be undone.`))return;
    const d=await API_CALL(`/api/admin/delete-user/${u._id}`,token,"DELETE");
    if(d.status){flash("🗑 Deleted");load();}
    else flash("❌ "+(d.message||"Failed"),false);
  };

  const filtered=users.filter(u=>{const t=search.toLowerCase();return !t||(u.phone||"").includes(t)||dn(u).toLowerCase().includes(t);});
  const totBal=users.reduce((s,u)=>s+(u.balance||0),0);

  return (
    <div>
      <h2 style={{color:"#ff4500",marginBottom:"20px"}}>👥 User Management</h2>
      {msg.text&&<Alert text={msg.text} ok={msg.ok}/>}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:"10px",marginBottom:"18px"}}>
        <KPI label="Total" val={users.length} color={S.blue}/>
        <KPI label="Active" val={users.filter(u=>u.isActive!==false).length} color={S.green}/>
        <KPI label="Suspended" val={users.filter(u=>u.isActive===false).length} color={S.red}/>
        <KPI label="Total Balance" val={`KSh ${fmt(totBal)}`} color={S.orange}/>
      </div>
      <div style={card()}>
        <div style={{display:"flex",gap:"10px",marginBottom:"14px",flexWrap:"wrap"}}>
          <Inp placeholder="Search phone or nickname…" value={search} onChange={setSearch} style={{flex:1,minWidth:"200px"}}/>
          <Btn label="↻ Refresh" onClick={load}/>
        </div>
        {loading?<div style={{textAlign:"center",color:"#555",padding:"40px"}}>Loading…</div>:(
          <Tbl cols={["Phone","Nickname","Balance","Role","Status","Joined","Last Login","Actions"]} rows={filtered.map(u=>[
            <span style={{color:"#888"}}>{u.phone||"—"}</span>,
            <strong>{dn(u)}</strong>,
            <span style={{color:u.balance>0?S.green:"#aaa"}}>KSh {fmt(u.balance||0)}</span>,
            <Badge text={u.role||"player"} color={u.role==="admin"?"blue":"green"}/>,
            <Badge text={u.isActive!==false?"Active":"Suspended"} color={u.isActive!==false?"green":"red"}/>,
            new Date(u.createdAt).toLocaleDateString("en-KE"),
            u.lastLogin?new Date(u.lastLogin).toLocaleDateString("en-KE"):"Never",
            <div style={{display:"flex",gap:"4px",flexWrap:"wrap"}}>
              <Btn label="👁" sm onClick={()=>setProf(u)}/>
              <Btn label="📊" sm onClick={()=>setHist(u)} color="purple"/>
              <Btn label="💰" sm onClick={()=>setDep({user:u,amt:""})} color="blue"/>
              <Btn label="✏️" sm onClick={()=>setBal({user:u,amt:String(u.balance)})} color="yellow"/>
              <Btn label="🔑" sm onClick={()=>setPw({user:u,val:""})} color="purple"/>
              {u.role!=="admin"&&<Btn label={u.isActive!==false?"🔒":"✅"} sm onClick={()=>doSuspend(u)} color={u.isActive!==false?"red":"green"}/>}
              {u.role!=="admin"&&<Btn label="🗑" sm onClick={()=>doDel(u)} color="red"/>}
            </div>
          ])}/>
        )}
      </div>
      {dep&&<Modal title={`💰 Deposit → ${dn(dep.user)}`} onClose={()=>setDep(null)}>
        <div style={{color:"#555",fontSize:"12px",marginBottom:"8px"}}>Current: KSh {fmt(dep.user.balance||0)}</div>
        <Inp placeholder="Amount (KSh)" type="number" value={dep.amt} onChange={(v:string)=>setDep(d=>d?{...d,amt:v}:null)} style={{width:"100%",marginBottom:"14px"}}/>
        <div style={{display:"flex",gap:"8px"}}><Btn label="Confirm" color="green" full onClick={doDeposit}/><Btn label="Cancel" full onClick={()=>setDep(null)}/></div>
      </Modal>}
      {bal&&<Modal title={`✏️ Set Balance — ${dn(bal.user)}`} onClose={()=>setBal(null)}>
        <div style={{color:"#555",fontSize:"12px",marginBottom:"8px"}}>Current: KSh {fmt(bal.user.balance||0)}</div>
        <Inp placeholder="New balance (KSh)" type="number" value={bal.amt} onChange={(v:string)=>setBal(d=>d?{...d,amt:v}:null)} style={{width:"100%",marginBottom:"14px"}}/>
        <div style={{display:"flex",gap:"8px"}}><Btn label="Set Balance" color="orange" full onClick={doSetBal}/><Btn label="Cancel" full onClick={()=>setBal(null)}/></div>
      </Modal>}
      {pw&&<Modal title={`🔑 Reset Password — ${dn(pw.user)}`} onClose={()=>setPw(null)}>
        <Inp placeholder="New password (min 6 chars)" type="password" value={pw.val} onChange={(v:string)=>setPw(d=>d?{...d,val:v}:null)} style={{width:"100%",marginBottom:"14px"}}/>
        <div style={{display:"flex",gap:"8px"}}><Btn label="Reset" color="purple" full onClick={doResetPw}/><Btn label="Cancel" full onClick={()=>setPw(null)}/></div>
      </Modal>}
      {prof&&<PlayerProfile token={token} user={prof} onClose={()=>setProf(null)}/>}
      {hist&&<UserHistory token={token} user={hist} onClose={()=>setHist(null)}/>}
    </div>
  );
}


/* ══════════════════════════ PLAYER PROFILE ════════════════════════════════ */
function PlayerProfile({token,user,onClose}:{token:string;user:User;onClose:()=>void}) {
  const [stats,setStats]=useState<any>(null);
  const [tab,setTab]=useState<"info"|"bets"|"withdrawals">("info");
  useEffect(()=>{
    API_CALL("/api/admin/users/"+user._id+"/stats",token).then((d:any)=>{if(d.status)setStats(d.data);});
  },[user._id,token]);
  const tabBtn=(t:string,label:string)=>(
    <button onClick={()=>setTab(t as any)} style={{padding:"6px 14px",borderRadius:"20px",border:"none",cursor:"pointer",fontSize:"12px",fontWeight:"bold",background:tab===t?"#ff4500":"#2a2a4e",color:"#fff"}}>{label}</button>
  );
  return (
    <Modal title={"Profile: "+dn(user)} onClose={onClose} width={560}>
      <div style={{display:"flex",gap:"6px",marginBottom:"16px"}}>
        {tabBtn("info","Info")}
        {tabBtn("bets","Bet History")}
        {tabBtn("withdrawals","Withdrawals")}
      </div>
      {tab==="info"&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px",marginBottom:"14px"}}>
            {([["Total Wagered","KSh "+fmt(Math.round(stats?.totalWagered||0)),S.orange],["Total Won","KSh "+fmt(Math.round(stats?.totalWon||0)),S.green],["Total Deposited","KSh "+fmt(Math.round(stats?.totalDeposited||0)),S.blue],["Deposits",stats?.depositCount||0,S.teal]] as [string,any,string][]).map(([l,v,c])=>(
              <div key={l} style={{background:"#0d0d1a",borderRadius:"8px",padding:"12px",textAlign:"center"}}>
                <div style={{color:"#555",fontSize:"10px",marginBottom:"4px"}}>{l}</div>
                <div style={{color:c,fontWeight:"bold",fontSize:"16px"}}>{v}</div>
              </div>
            ))}
          </div>
          {([["Phone",user.phone||"—"],["Balance","KSh "+fmt(user.balance||0)],["Bonus Balance","KSh "+fmt((user as any).bonusBalance||0)],["Wager Required","KSh "+fmt((user as any).wagerRequired||0)],["Referral Code",(user as any).referralCode||"—"],["Role",user.role||"player"],["Status",user.isActive!==false?"Active":"Suspended"],["Joined",new Date(user.createdAt).toLocaleString("en-KE")],["Last Login",user.lastLogin?new Date(user.lastLogin).toLocaleString("en-KE"):"Never"]] as [string,string][]).map(([l,v])=>(
            <div key={l} style={{display:"flex",justifyContent:"space-between",borderBottom:"1px solid #1a1a3e",padding:"7px 0",fontSize:"12px"}}><span style={{color:"#555"}}>{l}</span><span style={{color:"#ddd"}}>{v}</span></div>
          ))}
        </div>
      )}
      {tab==="bets"&&(
        <div style={{maxHeight:"320px",overflowY:"auto"}}>
          {!stats?.bets?.length&&<div style={{color:"#555",textAlign:"center",padding:"20px"}}>No bets yet</div>}
          {(stats?.bets||[]).map((b:any)=>(
            <div key={b._id} style={{display:"flex",justifyContent:"space-between",borderBottom:"1px solid #1a1a3e",padding:"7px 0",fontSize:"12px"}}>
              <span style={{color:"#555"}}>{new Date(b.createdAt).toLocaleString("en-KE")}</span>
              <span style={{color:S.red,fontWeight:"bold"}}>-KSh {fmt(Math.abs(b.amount))}</span>
              <span style={{color:"#444",fontSize:"11px"}}>{(b.roundId||"").slice(0,12)}</span>
            </div>
          ))}
        </div>
      )}
      {tab==="withdrawals"&&(
        <div style={{maxHeight:"320px",overflowY:"auto"}}>
          {!stats?.withdrawals?.length&&<div style={{color:"#555",textAlign:"center",padding:"20px"}}>No withdrawals yet</div>}
          {(stats?.withdrawals||[]).map((w:any)=>(
            <div key={w._id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid #1a1a3e",padding:"7px 0",fontSize:"12px"}}>
              <span style={{color:"#888"}}>{new Date(w.createdAt).toLocaleString("en-KE")}</span>
              <span style={{color:S.orange,fontWeight:"bold"}}>KSh {fmt(w.amount)}</span>
              <Badge text={w.status.toUpperCase()} color={w.status==="approved"?"green":w.status==="rejected"?"red":"yellow"}/>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}


/* ══════════════════════════ USER HISTORY ══════════════════════════════════ */
function UserHistory({token,user,onClose}:{token:string;user:User;onClose:()=>void}) {
  const [ledger,setLedger]=useState<any[]>([]);
  const [summary,setSummary]=useState<any>(null);
  const [loading,setLoading]=useState(false);
  const [preset,setPreset]=useState("today");
  const [from,setFrom]=useState("");
  const [to,setTo]=useState("");

  const getRange=(p:string)=>{
    const now=new Date();
    const fmt=(d:Date)=>d.toISOString().slice(0,10);
    if(p==="today") return {from:fmt(now),to:fmt(now)};
    if(p==="yesterday"){const y=new Date(now);y.setDate(y.getDate()-1);return {from:fmt(y),to:fmt(y)};}
    if(p==="7days"){const s=new Date(now);s.setDate(s.getDate()-6);return {from:fmt(s),to:fmt(now)};}
    return {from,to};
  };

  const load=async(p?:string)=>{
    setLoading(true);
    const range=getRange(p||preset);
    const url="/api/admin/users/"+user._id+"/history?from="+range.from+"&to="+range.to;
    const d=await API_CALL(url,token);
    if(d.status){setLedger(d.data||[]);setSummary(d.summary);}
    setLoading(false);
  };

  useEffect(()=>{load("today");},[user._id]);

  const typeColor=(t:string)=>t==="bet"?S.red:t==="win"?S.green:t==="deposit"?S.blue:t==="withdrawal"?S.orange:S.purple;

  return (
    <Modal title={"📊 History: "+dn(user)} onClose={onClose} width={640}>
      {/* Preset buttons */}
      <div style={{display:"flex",gap:"6px",marginBottom:"14px",flexWrap:"wrap"}}>
        {[["today","Today"],["yesterday","Yesterday"],["7days","Last 7 Days"],["custom","Custom"]].map(([p,l])=>(
          <button key={p} onClick={()=>{setPreset(p);if(p!=="custom")load(p);}} style={{padding:"6px 14px",borderRadius:"20px",border:"none",cursor:"pointer",fontSize:"12px",fontWeight:"bold",background:preset===p?"#ff4500":"#2a2a4e",color:"#fff"}}>{l}</button>
        ))}
      </div>
      {/* Custom date range */}
      {preset==="custom"&&(
        <div style={{display:"flex",gap:"8px",marginBottom:"14px",alignItems:"center"}}>
          <input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={{background:"#1a1a2e",border:"1px solid #333",borderRadius:"6px",padding:"7px",color:"#fff",fontSize:"12px"}}/>
          <span style={{color:"#555"}}>to</span>
          <input type="date" value={to} onChange={e=>setTo(e.target.value)} style={{background:"#1a1a2e",border:"1px solid #333",borderRadius:"6px",padding:"7px",color:"#fff",fontSize:"12px"}}/>
          <button onClick={()=>load("custom")} style={{background:"#ff4500",border:"none",borderRadius:"6px",padding:"7px 14px",color:"#fff",cursor:"pointer",fontSize:"12px",fontWeight:"bold"}}>Go</button>
        </div>
      )}
      {/* Summary KPIs */}
      {summary&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"8px",marginBottom:"14px"}}>
          {[["Bets",summary.totalBets,S.red],["Wagered","KSh "+fmt(Math.round(summary.totalWagered)),S.orange],["Won","KSh "+fmt(Math.round(summary.totalWon)),S.green],["Deposited","KSh "+fmt(Math.round(summary.totalDeposited)),S.blue]].map(([l,v,c])=>(
            <div key={l as string} style={{background:"#0d0d1a",borderRadius:"8px",padding:"10px",textAlign:"center"}}>
              <div style={{color:"#555",fontSize:"10px",marginBottom:"4px"}}>{l}</div>
              <div style={{color:c as string,fontWeight:"bold",fontSize:"14px"}}>{v}</div>
            </div>
          ))}
        </div>
      )}
      {/* Ledger table */}
      <div style={{maxHeight:"340px",overflowY:"auto"}}>
        {loading?<div style={{textAlign:"center",color:"#555",padding:"30px"}}>Loading…</div>:
         ledger.length===0?<div style={{textAlign:"center",color:"#555",padding:"30px"}}>No transactions in this period</div>:(
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
            <thead><tr>{["Time","Type","Amount","Bal After","Reference"].map(h=><th key={h} style={{padding:"7px",textAlign:"left",color:"#555",borderBottom:"1px solid #1a1a3e",fontSize:"11px"}}>{h}</th>)}</tr></thead>
            <tbody>{ledger.map((e:any)=>(
              <tr key={e._id} style={{borderBottom:"1px solid #0d0d1a"}}>
                <td style={{padding:"7px",color:"#555",whiteSpace:"nowrap"}}>{new Date(e.createdAt).toLocaleString("en-KE")}</td>
                <td style={{padding:"7px"}}><span style={{color:typeColor(e.type),fontWeight:"bold",textTransform:"uppercase",fontSize:"10px"}}>{e.type}</span></td>
                <td style={{padding:"7px",color:e.amount>=0?S.green:S.red,fontWeight:"bold"}}>{e.amount>=0?"+":""}{fmt(e.amount)}</td>
                <td style={{padding:"7px",color:"#888"}}>KSh {fmt(e.balanceAfter)}</td>
                <td style={{padding:"7px",color:"#444",fontSize:"10px"}}>{(e.reference||"").slice(0,20)}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}

/* ══════════════════════════ TRANSACTIONS ══════════════════════════════════ */
function Transactions({token}:{token:string}) {
  const [ledger,setLedger]=useState<Ledger[]>([]);
  const [loading,setLoading]=useState(false);
  const [typeF,setTypeF]=useState("all");
  const [dateF,setDateF]=useState("");
  const load=useCallback(async()=>{
    setLoading(true);
    const q=typeF!=="all"?`?type=${typeF}&limit=300`:"?limit=300";
    const d=await API_CALL(`/api/admin/ledger${q}`,token);
    setLedger(d.data||[]);setLoading(false);
  },[token,typeF]);
  useEffect(()=>{load();},[load]);
  const filtered=dateF?ledger.filter(e=>new Date(e.createdAt).toDateString()===new Date(dateF).toDateString()):ledger;
  const totIn=filtered.filter(e=>e.amount>0).reduce((s,e)=>s+e.amount,0);
  const totOut=filtered.filter(e=>e.amount<0).reduce((s,e)=>s+Math.abs(e.amount),0);
  return (
    <div>
      <h2 style={{color:"#ff4500",marginBottom:"20px"}}>💳 Wallet & Transactions</h2>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"10px",marginBottom:"18px"}}>
        <KPI label="Records" val={filtered.length} color={S.blue}/>
        <KPI label="Total In" val={`KSh ${fmt(totIn)}`} color={S.green}/>
        <KPI label="Total Out" val={`KSh ${fmt(totOut)}`} color={S.red}/>
      </div>
      <div style={card()}>
        <div style={{display:"flex",gap:"8px",marginBottom:"14px",flexWrap:"wrap",alignItems:"center"}}>
          {["all","bet","win","deposit","withdrawal","refund"].map(t=>(
            <button key={t} onClick={()=>setTypeF(t)} style={{padding:"5px 12px",borderRadius:"20px",border:"none",cursor:"pointer",fontSize:"12px",fontWeight:"bold",background:typeF===t?"#ff4500":"#2a2a4e",color:"#fff"}}>{t.toUpperCase()}</button>
          ))}
          <input type="date" value={dateF} onChange={e=>setDateF(e.target.value)} style={{background:"#0d0d1a",border:"1px solid #333",color:"#fff",padding:"5px 10px",borderRadius:"6px",fontSize:"12px"}}/>
          {dateF&&<Btn label="✕" sm onClick={()=>setDateF("")}/>}
          <Btn label="↻" onClick={load} sm/>
          <span style={{color:"#555",fontSize:"12px",marginLeft:"auto"}}>{filtered.length} records</span>
        </div>
        {loading?<div style={{textAlign:"center",color:"#555",padding:"40px"}}>Loading…</div>:(
          <Tbl cols={["Type","User","Amount","Bal Before","Bal After","Reference","Time"]} rows={filtered.slice(0,200).map(e=>{
            const u=typeof e.userId==="object"?(e.userId?.nickname||e.userId?.phone||"—"):"—";
            return [
              <Badge text={e.type.toUpperCase()} color={e.type==="win"||e.type==="deposit"||e.type==="refund"?"green":e.type==="bet"?"blue":"yellow"}/>,
              <span style={{color:"#888",fontSize:"12px"}}>{u}</span>,
              <span style={{color:e.amount<0?S.red:S.green,fontWeight:"bold"}}>{e.amount<0?"":"+"}KSh {fmt(Math.abs(e.amount))}</span>,
              <span style={{color:"#666"}}>KSh {fmt(e.balanceBefore)}</span>,
              <span style={{color:"#aaa"}}>KSh {fmt(e.balanceAfter)}</span>,
              <span style={{color:"#444",fontSize:"11px"}}>{(e.reference||"").slice(0,22)}</span>,
              <span style={{color:"#555",fontSize:"11px"}}>{new Date(e.createdAt).toLocaleString("en-KE")}</span>,
            ];
          })}/>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════ GAME CONTROL ══════════════════════════════════ */
function GameControl({token,stats,role}:{token:string;stats:Stats|null;role:string}) {
  const live=useLive(token);
  const [minB,setMinB]=useState("1");
  const [maxB,setMaxB]=useState("10000");
  const [msg,setMsg]=useState({text:"",ok:true});
  const flash=(text:string,ok=true)=>{setMsg({text,ok});setTimeout(()=>setMsg({text:"",ok:true}),4000);};
  const saveLimits=async()=>{const d=await API_CALL("/api/admin/game/limits",token,"POST",{min:parseFloat(minB),max:parseFloat(maxB)});if(d.status)flash(`✅ Limits updated: KSh ${minB} – KSh ${maxB}`);else flash("❌ "+(d.message||"Failed"),false);};
  const forceCrash=async()=>{if(!window.confirm("FORCE CRASH the current round immediately?"))return;const d=await API_CALL("/api/admin/game/force-crash",token,"POST");if(d.status)flash("💥 Force crash triggered!");else flash("❌ "+(d.message||"Cannot force crash now"),false);};
  const gs=stats?.currentRound;
  const staked=live.bettedUsers.reduce((s:number,u:any)=>s+(u.betAmount||0),0);
  return (
    <div>
      <h2 style={{color:"#ff4500",marginBottom:"20px"}}>🎮 Game Control Panel</h2>
      {msg.text&&<Alert text={msg.text} ok={msg.ok}/>}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"18px",marginBottom:"18px"}}>
        {/* Live state */}
        <div style={{...card(),borderColor:live.gameState==="PLAYING"?"#22c55e":"#2a2a4e"}}>
          <h3 style={{color:"#ff4500",marginBottom:"16px",fontSize:"14px"}}>⚡ Live Round</h3>
          {[
            ["State",<Badge text={live.gameState||"—"} color={live.gameState==="PLAYING"?"green":live.gameState==="BET"?"blue":"red"}/>],
            ["Multiplier",<span style={{color:S.yellow,fontSize:"28px",fontWeight:"bold"}}>{live.multiplier?.toFixed(2)}x</span>],
            ["Crash Point",<span style={{color:S.red,fontWeight:"bold"}}>{gs?.crashPoint}x</span>],
            ["Active Bets",<span style={{color:S.teal,fontWeight:"bold"}}>{live.bettedUsers.length}</span>],
            ["Total Staked",<span style={{color:S.orange}}>KSh {fmt(staked)}</span>],
            ["Round ID",<span style={{color:"#555",fontSize:"11px"}}>{gs?.roundId?.slice(0,16)}…</span>],
          ].map(([l,v])=>(
            <div key={l as string} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid #1a1a3e"}}><span style={{color:"#555",fontSize:"13px"}}>{l}</span>{v}</div>
          ))}
          <div style={{marginTop:"14px"}}>
            {role==="superadmin"?<Btn label="💥 Force Crash" color="red" full onClick={forceCrash} disabled={live.gameState!=="PLAYING"}/>:<div style={{textAlign:"center",color:"#444",fontSize:"12px",padding:"10px"}}>🔒 Superadmin only</div>}
            <div style={{color:"#444",fontSize:"11px",textAlign:"center",marginTop:"5px"}}>Ends round instantly · Super Admin only</div>
          </div>
        </div>
        {/* Bet limits */}
        <div style={card()}>
          <h3 style={{color:"#ff4500",marginBottom:"16px",fontSize:"14px"}}>⚙️ Bet Limits</h3>
          <div style={{marginBottom:"12px"}}><div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>Min Bet (KSh)</div><Inp value={minB} onChange={setMinB} type="number" style={{width:"100%"}}/></div>
          <div style={{marginBottom:"14px"}}><div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>Max Bet (KSh)</div><Inp value={maxB} onChange={setMaxB} type="number" style={{width:"100%"}}/></div>
          <Btn label="💾 Update Limits" color="orange" onClick={saveLimits} full/>
          <div style={{marginTop:"14px",padding:"12px",background:"#0d0d1a",borderRadius:"6px"}}>
            {[["Tick Speed","100ms"],["Bet Window","5s"],["Algorithm","Provably Fair"],["House Edge","~3%"]].map(([l,v])=>(
              <div key={l as string} style={{display:"flex",justifyContent:"space-between",fontSize:"12px",padding:"4px 0",borderBottom:"1px solid #1a1a3e",color:"#555"}}><span>{l}</span><span style={{color:"#aaa"}}>{v}</span></div>
            ))}
          </div>
        </div>
        {/* Revenue */}
        <div style={card()}>
          <h3 style={{color:"#ff4500",marginBottom:"14px",fontSize:"14px"}}>📊 Revenue Summary</h3>
          {[
            ["Total Wagered",`KSh ${fmt(stats?.totalWagered||0)}`,S.orange],
            ["Total Paid Out",`KSh ${fmt(stats?.totalPaidOut||0)}`,S.teal],
            ["Gross Profit",`KSh ${fmt((stats?.totalWagered||0)-(stats?.totalPaidOut||0))}`,(stats?.totalWagered||0)>=(stats?.totalPaidOut||0)?S.green:S.red],
            ["House Edge",stats?.houseEdge||"—",S.pink],
            ["Total Bets",fmt(stats?.totalBets||0),S.blue],
          ].map(([l,v,c])=>(
            <div key={l as string} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:"1px solid #1a1a3e",fontSize:"13px"}}><span style={{color:"#555"}}>{l}</span><span style={{color:c as string,fontWeight:"bold"}}>{v}</span></div>
          ))}
        </div>
        {/* Crash history dist */}
        <div style={card()}>
          <h3 style={{color:"#ff4500",marginBottom:"14px",fontSize:"14px"}}>🎯 Crash History (from stats)</h3>
          <div style={{fontSize:"13px",color:"#888",lineHeight:"2"}}>
            <div>Total rounds tracked: <span style={{color:"#fff"}}>{stats?.totalBets||0}</span></div>
            <div>Win rate: <span style={{color:S.teal}}>{stats?.totalBets?((((stats?.totalWins||0)/(stats?.totalBets||1))*100).toFixed(1))+"%":"—"}</span></div>
            <div>Avg bet: <span style={{color:S.orange}}>{stats?.totalBets?`KSh ${fmt(Math.round((stats?.totalWagered||0)/(stats?.totalBets||1)))}`:"—"}</span></div>
            <div>Avg win: <span style={{color:S.green}}>{stats?.totalWins?`KSh ${fmt(Math.round((stats?.totalPaidOut||0)/(stats?.totalWins||1)))}`:"—"}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════ PAYMENTS ══════════════════════════════════════ */
function Payments({token}:{token:string}) {
  const [wds,setWds]=useState<Withdrawal[]>([]);
  const [deps,setDeps]=useState<Ledger[]>([]);
  const [loading,setLoading]=useState(false);
  const [view,setView]=useState<"pending"|"all"|"deposits">("pending");
  const [approveM,setApproveM]=useState<Withdrawal|null>(null);
  const [mpesa,setMpesa]=useState("");
  const [rejectM,setRejectM]=useState<Withdrawal|null>(null);
  const [rejectR,setRejectR]=useState("");
  const [msg,setMsg]=useState({text:"",ok:true});
  const flash=(text:string,ok=true)=>{setMsg({text,ok});setTimeout(()=>setMsg({text:"",ok:true}),4000);};

  const load=useCallback(async()=>{
    setLoading(true);
    const [w,d]=await Promise.all([
      API_CALL(`/api/admin/withdrawals${view==="pending"?"?status=pending":""}`,token),
      API_CALL("/api/admin/ledger?type=deposit&limit=200",token)
    ]);
    setWds(w.data||[]);setDeps(d.data||[]);setLoading(false);
  },[token,view]);
  useEffect(()=>{load();const t=setInterval(()=>{if(view==="pending")load();},10000);return()=>clearInterval(t);},[load]);

  const doApprove=async()=>{
    if(!approveM)return;
    const d=await API_CALL(`/api/admin/withdrawals/${approveM._id}/approve`,token,"POST",{mpesaCode:mpesa});
    if(d.status){flash("✅ Withdrawal approved");setApproveM(null);load();}else flash("❌ "+(d.message||"Failed"),false);
  };
  const doReject=async()=>{
    if(!rejectM)return;
    const d=await API_CALL(`/api/admin/withdrawals/${rejectM._id}/reject`,token,"POST",{reason:rejectR});
    if(d.status){flash("✅ Rejected & funds refunded");setRejectM(null);load();}else flash("❌ "+(d.message||"Failed"),false);
  };

  const pending=wds.filter(w=>w.status==="pending");
  const todayDeps=deps.filter(d=>new Date(d.createdAt).toDateString()===new Date().toDateString());
  const todayTotal=todayDeps.reduce((s,d)=>s+d.amount,0);

  return (
    <div>
      <h2 style={{color:"#ff4500",marginBottom:"20px"}}>💰 Payments & Withdrawals</h2>
      {msg.text&&<Alert text={msg.text} ok={msg.ok}/>}
      {pending.length>0&&<div style={{...card({marginBottom:"16px",padding:"12px 18px"}),borderColor:S.yellow}}><span style={{color:S.yellow,fontWeight:"bold"}}>⚠️ {pending.length} pending withdrawal{pending.length>1?"s":""}</span><span style={{color:"#555",fontSize:"12px",marginLeft:"12px"}}>KSh {fmt(pending.reduce((s,w)=>s+w.amount,0))} total</span></div>}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:"10px",marginBottom:"16px"}}>
        <KPI label="Pending" val={pending.length} color={pending.length>0?S.yellow:S.green}/>
        <KPI label="All Withdrawals" val={wds.length} color={S.blue}/>
        <KPI label="Today Deposits" val={todayDeps.length} color={S.teal} sub={`KSh ${fmt(todayTotal)}`}/>
        <KPI label="All Deposits" val={deps.length} color={S.purple}/>
      </div>
      <div style={{display:"flex",gap:"8px",marginBottom:"14px"}}>
        {(["pending","all","deposits"] as const).map(t=>(
          <button key={t} onClick={()=>setView(t)} style={{padding:"7px 16px",borderRadius:"20px",border:"none",cursor:"pointer",fontSize:"12px",fontWeight:"bold",background:view===t?"#ff4500":"#2a2a4e",color:"#fff"}}>
            {t==="pending"?`⚡ Pending (${pending.length})`:t==="all"?"All Withdrawals":"Deposits"}
          </button>
        ))}
        <Btn label="↻" onClick={load} sm/>
      </div>
      <div style={card()}>
        {loading?<div style={{textAlign:"center",color:"#555",padding:"40px"}}>Loading…</div>:
         view==="deposits"?(
          <Tbl cols={["User","Amount","Bal After","Method","Reference","Time"]} rows={deps.map(e=>{
            const u=typeof e.userId==="object"?(e.userId?.nickname||e.userId?.phone||"—"):"—";
            return [<span style={{color:"#888"}}>{u}</span>,<span style={{color:S.green,fontWeight:"bold"}}>+KSh {fmt(e.amount)}</span>,`KSh ${fmt(e.balanceAfter)}`,<Badge text={e.reference?.startsWith("admin")?"MANUAL":"MPESA"} color={e.reference?.startsWith("admin")?"blue":"green"}/>,<span style={{color:"#444",fontSize:"11px"}}>{(e.reference||"").slice(0,22)}</span>,<span style={{color:"#555",fontSize:"11px"}}>{new Date(e.createdAt).toLocaleString("en-KE")}</span>];
          })}/>
         ):(
          <Tbl cols={["Ref","User","Phone","Amount","Status","Requested","Actions"]} rows={wds.map(w=>{
            const u=(w as any).user;
            return [
              <span style={{color:"#888",fontSize:"12px"}}>{w.reference}</span>,
              <strong>{u?dn(u):"—"}</strong>,
              <span style={{color:"#666"}}>{w.phone}</span>,
              <span style={{color:S.orange,fontWeight:"bold"}}>KSh {fmt(w.amount)}</span>,
              <Badge text={w.status.toUpperCase()} color={w.status==="approved"?"green":w.status==="rejected"?"red":"yellow"}/>,
              <span style={{color:"#555",fontSize:"11px"}}>{new Date(w.createdAt).toLocaleString("en-KE")}</span>,
              w.status==="pending"?(
                <div style={{display:"flex",gap:"4px"}}>
                  <Btn label="✅ Approve" sm color="green" onClick={()=>{setApproveM(w);setMpesa("");}}/>
                  <Btn label="❌ Reject" sm color="red" onClick={()=>{setRejectM(w);setRejectR("");}}/>
                </div>
              ):<span style={{color:"#555",fontSize:"11px"}}>{w.processedAt?new Date(w.processedAt).toLocaleDateString("en-KE"):"—"}</span>
            ];
          })}/>
         )}
      </div>
      {approveM&&<Modal title="✅ Approve Withdrawal" onClose={()=>setApproveM(null)}>
        <div style={{fontSize:"13px",marginBottom:"12px"}}>
          <div style={{color:"#888"}}>User: <strong style={{color:"#fff"}}>{(approveM as any).user?dn((approveM as any).user):approveM.phone}</strong></div>
          <div style={{color:S.orange,fontSize:"20px",fontWeight:"bold",margin:"8px 0"}}>KSh {fmt(approveM.amount)}</div>
          <div style={{color:"#555"}}>Phone: {approveM.phone}</div>
        </div>
        <Inp placeholder="M-Pesa code (optional)" value={mpesa} onChange={setMpesa} style={{width:"100%",marginBottom:"14px"}}/>
        <div style={{display:"flex",gap:"8px"}}><Btn label="Confirm Approval" color="green" full onClick={doApprove}/><Btn label="Cancel" full onClick={()=>setApproveM(null)}/></div>
      </Modal>}
      {rejectM&&<Modal title="❌ Reject Withdrawal" onClose={()=>setRejectM(null)}>
        <div style={{color:"#888",fontSize:"13px",marginBottom:"12px"}}>Amount: <strong style={{color:S.red}}>KSh {fmt(rejectM.amount)}</strong> — will be refunded to user.</div>
        <Inp placeholder="Reason (optional)" value={rejectR} onChange={setRejectR} style={{width:"100%",marginBottom:"14px"}}/>
        <div style={{display:"flex",gap:"8px"}}><Btn label="Reject & Refund" color="red" full onClick={doReject}/><Btn label="Cancel" full onClick={()=>setRejectM(null)}/></div>
      </Modal>}
    </div>
  );
}

/* ══════════════════════════ BETS HISTORY ══════════════════════════════════ */
function BetsHistory({token}:{token:string}) {
  const [entries,setEntries]=useState<Ledger[]>([]);
  const [loading,setLoading]=useState(false);
  const [filter,setFilter]=useState("all");
  const load=useCallback(async()=>{
    setLoading(true);
    const [b,w]=await Promise.all([API_CALL("/api/admin/ledger?type=bet&limit=300",token),API_CALL("/api/admin/ledger?type=win&limit=300",token)]);
    const all=[...(b.data||[]),...(w.data||[])].sort((a,b)=>new Date(b.createdAt).getTime()-new Date(a.createdAt).getTime());
    setEntries(all);setLoading(false);
  },[token]);
  useEffect(()=>{load();},[load]);
  const filtered=filter==="all"?entries:entries.filter(e=>e.type===filter);
  const bigWins=entries.filter(e=>e.type==="win"&&e.amount>5000);
  return (
    <div>
      <h2 style={{color:"#ff4500",marginBottom:"20px"}}>🎯 Bets & History</h2>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"10px",marginBottom:"18px"}}>
        <KPI label="Bet Records" val={entries.filter(e=>e.type==="bet").length} color={S.blue}/>
        <KPI label="Win Records" val={entries.filter(e=>e.type==="win").length} color={S.green}/>
        <KPI label="Big Wins >5K" val={bigWins.length} color={bigWins.length>5?S.red:S.yellow}/>
        <KPI label="High Bets >5K" val={entries.filter(e=>e.type==="bet"&&Math.abs(e.amount)>5000).length} color={S.orange}/>
      </div>
      <div style={card()}>
        <div style={{display:"flex",gap:"8px",marginBottom:"14px",alignItems:"center",flexWrap:"wrap"}}>
          {["all","bet","win"].map(f=><button key={f} onClick={()=>setFilter(f)} style={{padding:"6px 14px",borderRadius:"20px",border:"none",cursor:"pointer",fontSize:"12px",fontWeight:"bold",background:filter===f?"#ff4500":"#2a2a4e",color:"#fff"}}>{f.toUpperCase()}</button>)}
          <Btn label="↻" onClick={load} sm/>
          <span style={{color:"#555",fontSize:"12px",marginLeft:"auto"}}>{filtered.length} records</span>
        </div>
        {loading?<div style={{textAlign:"center",color:"#555",padding:"40px"}}>Loading…</div>:(
          <Tbl cols={["Type","User","Amount","Bal After","Round","Time"]} rows={filtered.slice(0,200).map(e=>{
            const u=typeof e.userId==="object"?(e.userId?.nickname||e.userId?.phone||"—"):"—";
            return [<Badge text={e.type.toUpperCase()} color={e.type==="win"?"green":"blue"}/>,<span style={{color:"#888",fontSize:"12px"}}>{u}</span>,<span style={{color:e.amount<0?S.red:S.green,fontWeight:"bold"}}>{e.amount<0?"":"+"}KSh {fmt(Math.abs(e.amount))}</span>,<span style={{color:"#aaa"}}>KSh {fmt(e.balanceAfter)}</span>,<span style={{color:"#444",fontSize:"11px"}}>{(e.roundId||"—").slice(0,12)}</span>,<span style={{color:"#555",fontSize:"11px"}}>{new Date(e.createdAt).toLocaleString("en-KE")}</span>];
          })}/>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════ RISK ══════════════════════════════════════════ */
function Risk({token}:{token:string}) {
  const [users,setUsers]=useState<User[]>([]);
  const [wins,setWins]=useState<Ledger[]>([]);
  const [loading,setLoading]=useState(true);
  useEffect(()=>{Promise.all([API_CALL("/api/admin/users",token).then((d:any)=>setUsers(d.data||[])),API_CALL("/api/admin/ledger?type=win&limit=500",token).then((d:any)=>setWins(d.data||[]))]).finally(()=>setLoading(false));},[token]);
  const whales=users.filter(u=>u.balance>500000&&u.role!=="admin");
  const bigWins=wins.filter(e=>e.amount>50000);
  const suspended=users.filter(u=>u.isActive===false);
  const rs=whales.length+bigWins.length;
  return (
    <div>
      <h2 style={{color:"#ff4500",marginBottom:"20px"}}>🚨 Risk & Fraud Detection</h2>
      {loading?<div style={{color:"#555"}}>Analyzing…</div>:(
        <>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"10px",marginBottom:"18px"}}>
            <KPI label="Risk Level" val={rs>5?"HIGH":rs>2?"MED":"LOW"} color={rs>5?S.red:rs>2?S.yellow:S.green}/>
            <KPI label="High Bal >500K" val={whales.length} color={whales.length>0?S.red:S.green}/>
            <KPI label="Big Wins >50K" val={bigWins.length} color={bigWins.length>0?S.yellow:S.green}/>
            <KPI label="Suspended" val={suspended.length} color={S.red}/>
          </div>
          {whales.length>0&&(
            <div style={{...card({marginBottom:"18px"}),borderColor:S.red}}>
              <h3 style={{color:S.red,marginBottom:"12px",fontSize:"14px"}}>⚠️ High Balance Users — Review Recommended</h3>
              <Tbl cols={["Phone","Nickname","Balance","Status"]} rows={whales.map(u=>[u.phone||"—",<strong>{dn(u)}</strong>,<span style={{color:S.red,fontWeight:"bold"}}>KSh {fmt(u.balance)}</span>,<Badge text={u.isActive!==false?"Active":"Suspended"} color={u.isActive!==false?"green":"red"}/>])}/>
            </div>
          )}
          {bigWins.length>0&&(
            <div style={{...card({marginBottom:"18px"}),borderColor:S.yellow}}>
              <h3 style={{color:S.yellow,marginBottom:"12px",fontSize:"14px"}}>💸 Large Cashouts (&gt; KSh 50,000)</h3>
              <Tbl cols={["Amount","Bal After","Round","Time"]} rows={bigWins.slice(0,20).map(e=>[<span style={{color:S.green,fontWeight:"bold"}}>+KSh {fmt(e.amount)}</span>,`KSh ${fmt(e.balanceAfter)}`,<span style={{fontSize:"11px",color:"#555"}}>{(e.roundId||"—").slice(0,14)}</span>,new Date(e.createdAt).toLocaleString("en-KE")])}/>
            </div>
          )}
          {!whales.length&&!bigWins.length&&<div style={{...card(),textAlign:"center",color:S.green,padding:"60px"}}><div style={{fontSize:"36px",marginBottom:"8px"}}>✅</div>No active risk flags</div>}
        </>
      )}
    </div>
  );
}

/* ══════════════════════════ REPORTS ════════════════════════════════════════ */
function Reports({token,stats}:{token:string;stats:Stats|null}) {
  const wag=stats?.totalWagered||0,paid=stats?.totalPaidOut||0,profit=wag-paid;
  const rtp=wag>0?((paid/wag)*100).toFixed(2):"0.00";
  const [daily,setDaily]=useState<any[]>([]);
  const [topPlayers,setTopPlayers]=useState<any[]>([]);
  useEffect(()=>{
    API_CALL("/api/admin/revenue/daily",token).then((d:any)=>{if(d.status)setDaily(d.data||[]);});
    API_CALL("/api/admin/top-players",token).then((d:any)=>{if(d.status)setTopPlayers(d.data||[]);});
  },[token]);
  const Bar=({label,val,max,color}:{label:string;val:number;max:number;color:string})=>(
    <div style={{marginBottom:"12px"}}>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:"12px",color:"#888",marginBottom:"4px"}}><span>{label}</span><span style={{color:"#fff"}}>KSh {fmt(val)}</span></div>
      <div style={{background:"#2a2a4e",borderRadius:"4px",height:"9px"}}><div style={{background:color,borderRadius:"4px",height:"9px",width:`${Math.min(100,(val/(max||1))*100)}%`,transition:"width 0.5s"}}/></div>
    </div>
  );
  const maxWag=Math.max(...daily.map(d=>d.wagered),1);
  return (
    <div>
      <h2 style={{color:"#ff4500",marginBottom:"20px"}}>📈 Reports & Analytics</h2>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:"10px",marginBottom:"22px"}}>
        {[["RTP",`${rtp}%`,S.green],["House Edge",stats?.houseEdge||"—",S.pink],["Gross Profit",`KSh ${fmt(profit)}`,profit>=0?S.green:S.red],["Total Bets",fmt(stats?.totalBets||0),S.blue],["Total Wins",fmt(stats?.totalWins||0),S.teal],["Win Rate",stats?.totalBets?`${(((stats?.totalWins||0)/(stats?.totalBets||1))*100).toFixed(1)}%`:"—",S.purple],["Avg Bet",stats?.totalBets?`KSh ${fmt(Math.round(wag/(stats?.totalBets||1)))}`:"—",S.orange],["Avg Win",stats?.totalWins&&paid>0?`KSh ${fmt(Math.round(paid/(stats?.totalWins||1)))}`:"—",S.teal]].map(([l,v,c])=>(
          <div key={l as string} style={card({textAlign:"center",padding:"14px"})}><div style={{color:"#555",fontSize:"10px",textTransform:"uppercase",marginBottom:"6px"}}>{l}</div><div style={{color:c as string,fontSize:"18px",fontWeight:"bold"}}>{v}</div></div>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"18px",marginBottom:"18px"}}>
        <div style={card()}><h3 style={{color:"#ff4500",marginBottom:"16px",fontSize:"14px"}}>💹 Revenue Breakdown</h3><Bar label="Total Wagered" val={wag} max={wag} color={S.orange}/><Bar label="Total Paid Out" val={paid} max={wag} color={S.teal}/><Bar label="House Profit" val={profit} max={wag} color={S.pink}/></div>
        <div style={card()}><h3 style={{color:"#ff4500",marginBottom:"16px",fontSize:"14px"}}>🎯 Bet vs Win</h3><Bar label="Bet Count" val={stats?.totalBets||0} max={stats?.totalBets||1} color={S.blue}/><Bar label="Win Count" val={stats?.totalWins||0} max={stats?.totalBets||1} color={S.green}/><div style={{marginTop:"14px",fontSize:"12px",color:"#555",lineHeight:"2"}}><div>B/W Ratio: <span style={{color:"#fff"}}>{stats?.totalBets&&stats?.totalWins?(stats.totalBets/stats.totalWins).toFixed(1):"-"}:1</span></div><div>RTP: <span style={{color:S.green}}>{rtp}%</span></div><div>Edge: <span style={{color:S.pink}}>{stats?.houseEdge||"—"}</span></div></div></div>
      </div>
      <div style={{...card(),marginBottom:"18px"}}>
        <h3 style={{color:"#ff4500",marginBottom:"16px",fontSize:"14px"}}>📅 Last 7 Days — Daily Revenue</h3>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
            <thead><tr>{["Date","Wagered","Paid Out","Deposited","Profit"].map(h=><th key={h} style={{padding:"8px",textAlign:"left",color:"#555",borderBottom:"1px solid #1a1a3e"}}>{h}</th>)}</tr></thead>
            <tbody>{daily.map((d:any)=>(
              <tr key={d.date}>
                <td style={{padding:"8px",color:"#888"}}>{d.date}</td>
                <td style={{padding:"8px",color:S.orange}}>KSh {fmt(d.wagered)}</td>
                <td style={{padding:"8px",color:S.teal}}>KSh {fmt(d.paidOut)}</td>
                <td style={{padding:"8px",color:S.blue}}>KSh {fmt(d.deposited)}</td>
                <td style={{padding:"8px",color:d.profit>=0?S.green:S.red,fontWeight:"bold"}}>KSh {fmt(d.profit)}</td>
              </tr>
            ))}</tbody>
            <tfoot><tr style={{borderTop:"2px solid #2a2a4e"}}>
              <td style={{padding:"8px",color:"#fff",fontWeight:"bold"}}>TOTAL</td>
              <td style={{padding:"8px",color:S.orange,fontWeight:"bold"}}>KSh {fmt(daily.reduce((s,d)=>s+d.wagered,0))}</td>
              <td style={{padding:"8px",color:S.teal,fontWeight:"bold"}}>KSh {fmt(daily.reduce((s,d)=>s+d.paidOut,0))}</td>
              <td style={{padding:"8px",color:S.blue,fontWeight:"bold"}}>KSh {fmt(daily.reduce((s,d)=>s+d.deposited,0))}</td>
              <td style={{padding:"8px",color:S.green,fontWeight:"bold"}}>KSh {fmt(daily.reduce((s,d)=>s+d.profit,0))}</td>
            </tr></tfoot>
          </table>
          <div style={{display:"flex",alignItems:"flex-end",gap:"4px",height:"80px",marginTop:"16px",padding:"0 4px"}}>
            {daily.map((d:any)=>(
              <div key={d.date} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:"2px"}}>
                <div style={{width:"100%",background:S.orange,borderRadius:"2px 2px 0 0",height:`${Math.max(4,(d.wagered/maxWag)*70)}px`,transition:"height 0.5s"}} title={`Wagered: KSh ${fmt(d.wagered)}`}/>
                <div style={{fontSize:"9px",color:"#444",transform:"rotate(-30deg)",whiteSpace:"nowrap"}}>{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={card()}>
        <h3 style={{color:"#ff4500",marginBottom:"16px",fontSize:"14px"}}>🏆 Top 10 Players by Volume</h3>
        <Tbl cols={["Rank","Player","Phone","Total Wagered","Bet Count"]} rows={topPlayers.map((p:any,i:number)=>[
          <span style={{color:i===0?S.yellow:i===1?"#aaa":i===2?"#cd7f32":"#555",fontWeight:"bold"}}>#{i+1}</span>,
          <strong>{p.user?dn(p.user):"—"}</strong>,
          <span style={{color:"#666"}}>{p.user?.phone||"—"}</span>,
          <span style={{color:S.orange,fontWeight:"bold"}}>KSh {fmt(Math.round(p.totalWagered))}</span>,
          <span style={{color:S.blue}}>{fmt(p.betCount)}</span>
        ])}/>
      </div>
    </div>
  );
}

/* ══════════════════════════ MPESA SETTINGS ════════════════════════════════ */
function MpesaSettings({token}:{token:string}) {
  const [keys, setKeys] = React.useState({
    MPESA_CONSUMER_KEY:"", MPESA_CONSUMER_SECRET:"",
    MPESA_SHORTCODE:"", MPESA_PASSKEY:"",
    MPESA_CALLBACK_BASE_URL:"", MPESA_ENV:"sandbox"
  });
  const [loading, setLoading] = React.useState(true);
  const [msg, setMsg] = React.useState({text:"",ok:true});
  const flash = (text:string, ok=true) => { setMsg({text,ok}); setTimeout(()=>setMsg({text:"",ok:true}),3500); };

  React.useEffect(()=>{
    API_CALL("/api/admin/mpesa-settings", token).then((d:any)=>{
      if(d.status) setKeys(d.data);
      setLoading(false);
    });
  },[token]);

  const save = async () => {
    const d = await API_CALL("/api/admin/mpesa-settings", token, "POST", keys);
    if(d.status) flash("✅ M-Pesa settings saved");
    else flash("❌ "+(d.message||"Failed"), false);
  };

  if(loading) return <div style={{color:"#555",fontSize:"12px"}}>Loading…</div>;

  const fields:[string,keyof typeof keys][] = [
    ["Consumer Key","MPESA_CONSUMER_KEY"],
    ["Consumer Secret","MPESA_CONSUMER_SECRET"],
    ["Shortcode","MPESA_SHORTCODE"],
    ["Passkey","MPESA_PASSKEY"],
    ["Callback Base URL","MPESA_CALLBACK_BASE_URL"],
  ];

  return (
    <div>
      {msg.text && <Alert text={msg.text} ok={msg.ok}/>}
      {fields.map(([label, key])=>(
        <div key={key} style={{marginBottom:"10px"}}>
          <div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>{label}</div>
          <Inp value={keys[key]} onChange={(v:string)=>setKeys(k=>({...k,[key]:v}))} style={{width:"100%"}}/>
        </div>
      ))}
      <div style={{marginBottom:"10px"}}>
        <div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>Environment</div>
        <select value={keys.MPESA_ENV} onChange={e=>setKeys(k=>({...k,MPESA_ENV:e.target.value}))}
          style={{width:"100%",background:"#12122a",color:"#ccc",border:"1px solid #2a2a4e",borderRadius:"6px",padding:"8px",fontSize:"13px"}}>
          <option value="sandbox">Sandbox</option>
          <option value="production">Production</option>
        </select>
      </div>
      <Btn label="💾 Save M-Pesa Keys" color="orange" onClick={save} full/>
    </div>
  );
}

/* ══════════════════════════ SETTINGS ══════════════════════════════════════ */
function Settings({token}:{token:string}) {
  const [minB,setMinB]=useState("1");
  const [maxB,setMaxB]=useState("10000");
  const [refBonus,setRefBonus]=useState("200");
  const [wagerMult,setWagerMult]=useState("5");
  const [dailyBonusAmt,setDailyBonusAmt]=useState("50");
  const [crashAlgo,setCrashAlgo]=useState("hmac-sha256");
  const [growthCurve,setGrowthCurve]=useState("exponential");
  const [houseEdge,setHouseEdge]=useState("3");
  const [betWindow,setBetWindow]=useState("5");
  const [tickSpeed,setTickSpeed]=useState("100");
  const [msg,setMsg]=useState({text:"",ok:true});
  const [server,setServer]=useState<any>(null);
  const flash=(text:string,ok=true)=>{setMsg({text,ok});setTimeout(()=>setMsg({text:"",ok:true}),3500);};
  const save=async()=>{const d=await API_CALL("/api/admin/game/limits",token,"POST",{min:parseFloat(minB),max:parseFloat(maxB)});if(d.status)flash("✅ Saved");else flash("❌ "+(d.message||"Failed"),false);};
  const saveBonusSettings=async()=>{const d=await API_CALL("/api/admin/settings",token,"POST",{referralBonus:parseFloat(refBonus),wagerMultiplier:parseFloat(wagerMult),dailyBonus:parseFloat(dailyBonusAmt),crashAlgorithm:crashAlgo,growthCurve:growthCurve,houseEdgePct:parseFloat(houseEdge),betWindow:parseFloat(betWindow)});if(d.status)flash("✅ Bonus settings saved");else flash("❌ "+(d.message||"Failed"),false);};
  useEffect(()=>{API_CALL("/api/admin/settings",token).then((d:any)=>{if(d.status){setRefBonus(String(d.data.referralBonus||200));setWagerMult(String(d.data.wagerMultiplier||5));setDailyBonusAmt(String(d.data.dailyBonus||50));setCrashAlgo(d.data.crashAlgorithm||"hmac-sha256");setGrowthCurve(d.data.growthCurve||"exponential");setHouseEdge(String(d.data.houseEdgePct||3));setBetWindow(String(d.data.betWindow||5));setTickSpeed(String(d.data.tickSpeed||100));}});},[token]);
  useEffect(()=>{
    const load=()=>API_CALL("/api/admin/server-stats",token).then((d:any)=>{if(d.status)setServer(d);});
    load();const t=setInterval(load,5000);return()=>clearInterval(t);
  },[token]);
  return (
    <div>
      <h2 style={{color:"#ff4500",marginBottom:"20px"}}>⚙️ System Settings</h2>
      {msg.text&&<Alert text={msg.text} ok={msg.ok}/>}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"18px"}}>
        <div style={card()}>
          <h3 style={{color:"#ff4500",marginBottom:"14px",fontSize:"14px"}}>🎮 Game Settings</h3>
          <div style={{marginBottom:"10px"}}><div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>Min Bet (KSh)</div><Inp value={minB} onChange={setMinB} type="number" style={{width:"100%"}}/></div>
          <div style={{marginBottom:"14px"}}><div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>Max Bet (KSh)</div><Inp value={maxB} onChange={setMaxB} type="number" style={{width:"100%"}}/></div>
          <Btn label="💾 Save" color="orange" onClick={save} full/>
          <div style={{marginTop:"16px",borderTop:"1px solid #252545",paddingTop:"14px"}}>
            <div style={{color:"#555",fontSize:"10px",textTransform:"uppercase",letterSpacing:"1px",marginBottom:"10px",fontWeight:600}}>Current Engine Config</div>
            {[
              ["⚡ Tick Speed", betWindow?"—":(tickSpeed||"100")+"ms"],
              ["⏱ Bet Window", (betWindow||"5")+"s"],
              ["🔐 Algorithm", crashAlgo==="hmac-sha256"?"HMAC-SHA256":crashAlgo==="hash-chain"?"Hash Chain":"Weighted Random"],
              ["📊 House Edge", (houseEdge||"3")+"%"],
              ["📈 Growth Curve", growthCurve==="exponential"?"Exponential":growthCurve==="fast-exponential"?"Fast Exp":growthCurve==="linear"?"Linear":"Turbo"],
            ].map(([l,v])=>(
              <div key={l as string} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #1a1a3e",fontSize:"12px"}}>
                <span style={{color:"#555"}}>{l}</span>
                <span style={{color:"#e2e8f0",fontWeight:600}}>{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={card()}>
          <h3 style={{color:"#ff4500",marginBottom:"14px",fontSize:"14px"}}>🎁 Bonus & Referral Settings</h3>
          <div style={{marginBottom:"10px"}}><div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>Referral Bonus (KSh) — given to both players</div><Inp value={refBonus} onChange={setRefBonus} type="number" style={{width:"100%"}}/></div>
          <div style={{marginBottom:"10px"}}><div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>Wager Multiplier — e.g. 5 means wager 5x bonus to unlock</div><Inp value={wagerMult} onChange={setWagerMult} type="number" style={{width:"100%"}}/></div>
          <div style={{marginBottom:"14px"}}><div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>Daily Bonus Amount (KSh)</div><Inp value={dailyBonusAmt} onChange={setDailyBonusAmt} type="number" style={{width:"100%"}}/></div>
          <div style={{marginBottom:"10px"}}><div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>House Edge % (e.g. 3 = 97% RTP)</div><Inp value={houseEdge} onChange={setHouseEdge} type="number" style={{width:"100%"}}/></div>
          <div style={{marginBottom:"10px"}}><div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>Bet Window (seconds)</div><Inp value={betWindow} onChange={setBetWindow} type="number" style={{width:"100%"}}/></div>
          <div style={{marginBottom:"10px"}}><div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>Crash Algorithm</div>
            <select value={crashAlgo} onChange={e=>setCrashAlgo(e.target.value)} style={{width:"100%",padding:"10px",background:"#1a1a2e",border:"1px solid #333",borderRadius:"6px",color:"#fff",fontSize:"13px"}}>
              <option value="hmac-sha256">HMAC-SHA256 (Provably Fair)</option>
              <option value="hash-chain">Hash Chain (Bustabit-style)</option>
              <option value="weighted-random">Weighted Random (House Controlled)</option>
            </select>
          </div>
          <div style={{marginBottom:"14px"}}><div style={{color:"#555",fontSize:"12px",marginBottom:"4px"}}>Multiplier Growth Curve</div>
            <select value={growthCurve} onChange={e=>setGrowthCurve(e.target.value)} style={{width:"100%",padding:"10px",background:"#1a1a2e",border:"1px solid #333",borderRadius:"6px",color:"#fff",fontSize:"13px"}}>
              <option value="exponential">Exponential e^(0.08t) — Standard</option>
              <option value="fast-exponential">Fast Exponential e^(0.12t) — More Tension</option>
              <option value="linear">Linear 1+0.1t — Steady Climb</option>
              <option value="turbo">Turbo e^(0.20t) — Very Aggressive</option>
            </select>
          </div>
          <Btn label="💾 Save Bonus Settings" color="orange" onClick={saveBonusSettings} full/>
        </div>
        <div style={card()}>
          <h3 style={{color:"#ff4500",marginBottom:"14px",fontSize:"14px"}}>💳 M-Pesa (Daraja)</h3>
          <MpesaSettings token={token}/>
        </div>
        <div style={card()}>
          <h3 style={{color:"#ff4500",marginBottom:"14px",fontSize:"14px"}}>🔐 Security Status</h3>
          {[["JWT Auth","✅"],["Rate Limiting","✅"],["Helmet Headers","✅"],["Balance Validation","✅"],["Transaction Ledger","✅"],["Socket Auth","✅"],["M-Pesa Webhook","⏳"],["SSL / HTTPS","⏳"]].map(([l,v])=>(
            <div key={l as string} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #1a1a3e",fontSize:"13px"}}><span style={{color:"#555"}}>{l}</span><span style={{color:(v as string).startsWith("✅")?S.green:S.yellow}}>{v}</span></div>
          ))}
        </div>
        <div style={card()}>
          <h3 style={{color:"#ff4500",marginBottom:"14px",fontSize:"14px"}}>🖥️ Server Load</h3>
          {server?(
            <>
              <div style={{marginBottom:"10px"}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:"12px",color:"#888",marginBottom:"4px"}}><span>CPU Load (1m)</span><span style={{color:parseFloat(server.cpu?.loadAvg?.["1m"])>3?S.red:S.green}}>{server.cpu?.loadAvg?.["1m"]}</span></div>
                <div style={{background:"#2a2a4e",borderRadius:"4px",height:"8px"}}><div style={{background:parseFloat(server.cpu?.loadAvg?.["1m"])>3?S.red:S.green,borderRadius:"4px",height:"8px",width:`${Math.min(100,(parseFloat(server.cpu?.loadAvg?.["1m"])/server.cpu?.cores)*100)}%`,transition:"width 0.5s"}}/></div>
              </div>
              <div style={{marginBottom:"10px"}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:"12px",color:"#888",marginBottom:"4px"}}><span>RAM Usage</span><span style={{color:parseFloat(server.memory?.pct)>85?S.red:S.green}}>{server.memory?.pct}%</span></div>
                <div style={{background:"#2a2a4e",borderRadius:"4px",height:"8px"}}><div style={{background:parseFloat(server.memory?.pct)>85?S.red:S.green,borderRadius:"4px",height:"8px",width:`${server.memory?.pct}%`,transition:"width 0.5s"}}/></div>
              </div>
              {[
                ["RAM Used",`${server.memory?.used} MB / ${server.memory?.total} MB`,S.teal],
                ["CPU Cores",server.cpu?.cores,S.blue],
                ["Load 5m / 15m",`${server.cpu?.loadAvg?.["5m"]} / ${server.cpu?.loadAvg?.["15m"]}`,S.orange],
                ["Uptime",server.uptime?.human,S.green],
                ["Node.js",server.node,S.purple],
                ["Platform",server.platform,S.gray],
              ].map(([l,v,c])=>(
                <div key={l as string} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid #1a1a3e",fontSize:"12px"}}><span style={{color:"#555"}}>{l}</span><span style={{color:c as string}}>{v}</span></div>
              ))}
            </>
          ):<div style={{color:"#555",fontSize:"12px"}}>Loading server stats…</div>}
        </div>
        <div style={card()}>
          <h3 style={{color:"#ff4500",marginBottom:"14px",fontSize:"14px"}}>ℹ️ System Info</h3>
          {[["Backend","Node.js + Express"],["DB","MongoDB"],["Realtime","Socket.io"],["Frontend","React + TypeScript"],["Currency","KSh"],["Admin API",`${API}/api/admin/*`]].map(([l,v])=>(
            <div key={l as string} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #1a1a3e",fontSize:"13px"}}><span style={{color:"#555"}}>{l}</span><span style={{color:"#aaa",fontSize:"12px"}}>{v}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════ MAIN ══════════════════════════════════════════ */
const NAV=[
  {id:"dashboard",label:"Dashboard",icon:"📊"},
  {id:"users",label:"Users",icon:"👥"},
  {id:"transactions",label:"Transactions",icon:"💳"},
  {id:"game",label:"Game Control",icon:"🎮"},
  {id:"bets",label:"Bets & History",icon:"🎯"},
  {id:"payments",label:"Payments",icon:"💰"},
  {id:"risk",label:"Risk & Fraud",icon:"🚨"},
  {id:"reports",label:"Reports",icon:"📈"},
  {id:"settings",label:"Settings",icon:"⚙️"},
];

export default function AdminPanel() {
  const [token,setToken]=useState(()=>localStorage.getItem("admin-token")||"");
  const [authed,setAuthed]=useState(false);
  const [phone,setPhone]=useState("");
  const [pass,setPass]=useState("");
  const [loginErr,setLoginErr]=useState("");
  const [tab,setTab]=useState("dashboard");
  const [stats,setStats]=useState<Stats|null>(null);
  const [collapsed,setCollapsed]=useState(false);
  const [role,setRole]=useState("admin");

  useEffect(()=>{
    if(!token)return;
    fetch(`${API}/api/admin/stats`,{headers:{Authorization:`Bearer ${token}`}}).then(r=>r.ok?r.json():null).then(d=>{if(d){setStats(d);setAuthed(true);try{const p=JSON.parse(atob(token.split(".")[1]));setRole(p.role||"admin");}catch(e){}}else{localStorage.removeItem("admin-token");setToken("");}}).catch(()=>{});
  },[]);

  useEffect(()=>{
    if(!authed||!token)return;
    const t=setInterval(()=>{fetch(`${API}/api/admin/stats`,{headers:{Authorization:`Bearer ${token}`}}).then(r=>r.ok?r.json():null).then(d=>{if(d)setStats(d);}).catch(()=>{});},4000);
    return()=>clearInterval(t);
  },[authed,token]);

  const doLogin=async()=>{
    setLoginErr("");
    if(!phone||!pass){setLoginErr("Fill in all fields");return;}
    const body=/^0\d{9}$/.test(phone)?{phone,password:pass}:{username:phone,password:pass};
    try {
      const res=await fetch(`${API}/api/login`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
      const data=await res.json();
      if(!data.token){setLoginErr(data.message||"Login failed");return;}
      const chk=await fetch(`${API}/api/admin/stats`,{headers:{Authorization:`Bearer ${data.token}`}});
      if(!chk.ok){setLoginErr("Access denied — admin only");return;}
      const sd=await chk.json();
      localStorage.setItem("admin-token",data.token);
      setToken(data.token);setStats(sd);setAuthed(true);
      try{const p=JSON.parse(atob(data.token.split(".")[1]));setRole(p.role||"admin");}catch(e){}
    } catch{setLoginErr("Connection error");}
  };

  const doLogout=()=>{localStorage.removeItem("admin-token");setToken("");setAuthed(false);setStats(null);};

  if(!authed) return (
    <div style={{minHeight:"100vh",background:"radial-gradient(ellipse at top,#1a0a00 0%,#0d0d1a 60%)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Segoe UI',sans-serif",padding:"20px"}}>
      <div style={{width:"100%",maxWidth:"380px"}}>
        <div style={{textAlign:"center",marginBottom:"36px"}}>
          <div style={{width:"72px",height:"72px",background:"linear-gradient(135deg,#ff4500,#ff6a00)",borderRadius:"20px",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"36px",margin:"0 auto 16px",boxShadow:"0 8px 32px rgba(255,69,0,0.4)"}}>✈️</div>
          <div style={{color:"#fff",fontSize:"24px",fontWeight:"bold",letterSpacing:"-0.5px"}}>Avitex Admin</div>
          <div style={{color:"#555",fontSize:"12px",marginTop:"6px",letterSpacing:"1px",textTransform:"uppercase"}}>Operations Command Centre</div>
        </div>
        <div style={{background:"linear-gradient(160deg,#1e1e38,#151525)",border:"1px solid #252545",borderRadius:"16px",padding:"32px",boxShadow:"0 24px 80px rgba(0,0,0,0.6)"}}>
          <div style={{marginBottom:"16px"}}>
            <div style={{color:"#666",fontSize:"11px",marginBottom:"6px",textTransform:"uppercase",letterSpacing:"0.8px"}}>Phone / Username</div>
            <input type="text" placeholder="0712345678 or admin" value={phone} onChange={e=>setPhone(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doLogin()} style={{width:"100%",padding:"12px 14px",background:"rgba(0,0,0,0.4)",border:"1px solid #252545",borderRadius:"8px",color:"#fff",fontSize:"14px",boxSizing:"border-box" as any,outline:"none"}}/>
          </div>
          <div style={{marginBottom:"20px"}}>
            <div style={{color:"#666",fontSize:"11px",marginBottom:"6px",textTransform:"uppercase",letterSpacing:"0.8px"}}>Password</div>
            <input type="password" placeholder="••••••••" value={pass} onChange={e=>setPass(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doLogin()} style={{width:"100%",padding:"12px 14px",background:"rgba(0,0,0,0.4)",border:"1px solid #252545",borderRadius:"8px",color:"#fff",fontSize:"14px",boxSizing:"border-box" as any,outline:"none"}}/>
          </div>
          {loginErr&&<div style={{color:S.red,fontSize:"12px",marginBottom:"14px",padding:"10px",background:"rgba(239,68,68,0.1)",borderRadius:"6px"}}>⚠️ {loginErr}</div>}
          <button onClick={doLogin} style={{width:"100%",padding:"14px",background:"linear-gradient(135deg,#ff4500,#ff6a00)",color:"#fff",border:"none",borderRadius:"10px",fontWeight:"bold",cursor:"pointer",fontSize:"15px",boxShadow:"0 4px 20px rgba(255,69,0,0.4)",letterSpacing:"0.3px"}}>Sign In →</button>
        </div>
        <div style={{textAlign:"center",marginTop:"20px",color:"#333",fontSize:"11px"}}>🔒 Secured · Avitex Platform 2026</div>
      </div>
    </div>
  );

  return (
    <div style={{display:"flex",minHeight:"100vh",background:"#0d0d1a",fontFamily:"'Segoe UI',sans-serif",color:"#fff"}}>
      {/* Sidebar */}
      <div style={{width:collapsed?"60px":"220px",background:"linear-gradient(180deg,#12122a 0%,#0e0e20 100%)",borderRight:"1px solid #1a1a3e",flexShrink:0,display:"flex",flexDirection:"column",transition:"width 0.25s cubic-bezier(0.4,0,0.2,1)",overflow:"hidden",boxShadow:"4px 0 24px rgba(0,0,0,0.3)"}}>
        <div style={{padding:"16px 14px",borderBottom:"1px solid #1a1a3e",display:"flex",alignItems:"center",gap:"12px",background:"linear-gradient(90deg,rgba(255,69,0,0.06),transparent)"}}>
          <button onClick={()=>setCollapsed(!collapsed)} style={{background:"rgba(255,69,0,0.1)",border:"1px solid rgba(255,69,0,0.2)",color:"#ff4500",fontSize:"16px",cursor:"pointer",lineHeight:1,padding:"6px",borderRadius:"6px",flexShrink:0,width:"32px",height:"32px",display:"flex",alignItems:"center",justifyContent:"center"}}>☰</button>
          {!collapsed&&<div><div style={{color:"#ff4500",fontWeight:"bold",fontSize:"14px",whiteSpace:"nowrap",letterSpacing:"-0.3px"}}>✈ Avitex</div><div style={{color:"#333",fontSize:"9px",letterSpacing:"1px",textTransform:"uppercase"}}>Admin Panel</div></div>}
        </div>
        <nav style={{flex:1,padding:"10px 8px",overflowY:"auto"}}>
          {NAV.map(n=>(
            <button key={n.id} onClick={()=>setTab(n.id)} title={collapsed?n.label:undefined} style={{width:"100%",display:"flex",alignItems:"center",gap:"10px",padding:collapsed?"10px":"10px 12px",background:tab===n.id?"linear-gradient(90deg,rgba(255,69,0,0.15),rgba(255,69,0,0.05))":"none",border:"none",borderLeft:tab===n.id?"3px solid #ff4500":"3px solid transparent",borderRadius:"0 8px 8px 0",color:tab===n.id?"#ff4500":"#555",cursor:"pointer",fontSize:"13px",textAlign:"left",justifyContent:collapsed?"center":"flex-start",marginBottom:"2px",transition:"all 0.15s"}}>
              <span style={{fontSize:"16px",flexShrink:0,filter:tab===n.id?"drop-shadow(0 0 6px #ff4500)":"none"}}>{n.icon}</span>
              {!collapsed&&<span style={{whiteSpace:"nowrap",fontWeight:tab===n.id?"600":"400"}}>{n.label}</span>}
            </button>
          ))}
        </nav>
        {!collapsed&&<div style={{padding:"12px 14px",borderTop:"1px solid #1a1a3e",fontSize:"10px"}}>
          <div style={{display:"flex",alignItems:"center",gap:"6px",marginBottom:"6px"}}>
            <span style={{width:"6px",height:"6px",borderRadius:"50%",background:S.green,boxShadow:"0 0 6px "+S.green,flexShrink:0}}/>
            <span style={{color:S.green,fontWeight:600}}>System Online</span>
          </div>
          <div style={{color:"#333",lineHeight:"1.6"}}>{stats?.totalUsers||0} users<br/>{stats?.totalBets||0} total bets</div>
        </div>}
      </div>
      {/* Content */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{background:"linear-gradient(90deg,#12122a,#111128)",borderBottom:"1px solid #1a1a3e",padding:"12px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0,boxShadow:"0 2px 12px rgba(0,0,0,0.3)"}}>
          <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
            <span style={{fontSize:"16px"}}>{NAV.find(n=>n.id===tab)?.icon}</span>
            <div>
              <div style={{fontSize:"13px",color:"#e2e8f0",fontWeight:600}}>{NAV.find(n=>n.id===tab)?.label}</div>
              <div style={{fontSize:"10px",color:"#444",letterSpacing:"0.5px"}}>Avitex Operations</div>
            </div>
          </div>
          <div style={{display:"flex",gap:"12px",alignItems:"center"}}>
            <div style={{display:"flex",alignItems:"center",gap:"6px",background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.2)",padding:"5px 12px",borderRadius:"20px"}}>
              <span style={{width:"6px",height:"6px",borderRadius:"50%",background:S.green,boxShadow:"0 0 8px "+S.green}}/>
              <span style={{color:S.green,fontSize:"11px",fontWeight:600}}>Live</span>
            </div>
            {stats?.currentRound&&<div style={{background:"rgba(250,204,21,0.08)",border:"1px solid rgba(250,204,21,0.2)",padding:"5px 12px",borderRadius:"20px"}}>
              <span style={{color:S.yellow,fontSize:"11px",fontWeight:600}}>{stats.currentRound.GameState} · {stats.currentRound.multiplier}x</span>
            </div>}
            <button onClick={doLogout} style={{background:"rgba(255,69,0,0.1)",border:"1px solid rgba(255,69,0,0.2)",color:"#ff4500",padding:"6px 14px",borderRadius:"8px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>Logout</button>
          </div>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"24px",background:"#0d0d1a"}}>
          {tab==="dashboard"&&<Dashboard token={token} stats={stats}/>}
          {tab==="users"&&<Users token={token}/>}
          {tab==="transactions"&&<Transactions token={token}/>}
          {tab==="game"&&<GameControl token={token} stats={stats} role={role}/>}
          {tab==="bets"&&<BetsHistory token={token}/>}
          {tab==="payments"&&<Payments token={token}/>}
          {tab==="risk"&&<Risk token={token}/>}
          {tab==="reports"&&<Reports token={token} stats={stats}/>}
          {tab==="settings"&&<Settings token={token}/>}
        </div>
      </div>
    </div>
  );
}
